import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { loadVerbsCsv } from './utils/loadVerbsCsv';
import { conjugatePastGeneric_Salim } from './utils/arPastGeneric_salim';
import { conjugatePresentFormI_Salim_fromRow } from './utils/arPresentFormI_salim';
import { conjugatePresentFormIV_Salim_fromRow } from './utils/arPresentFormIV_salim';
import { buildImperativeFromRow } from './utils/imperativeFormI_salim';
import { buildImperativeFromRowIII } from './utils/imperativeFormIII_salim';
import { buildImperativeFromRowIV } from './utils/imperativeFormIV_salim';
import { buildParticiplesFromRoot } from './utils/participlesFormI_salim';
import { buildParticiplesFromRowNonFormI } from './utils/participlesNonFormI';
import { buildGeneratedMasdar } from './utils/masdarPatterns';
import './App.css';
import './ParticiplesGame.css';
import './MasdarBalloonGame.css';

const TARGET_COUNT = 10;
const MOBILE_TARGET_ONSCREEN_BALLOON_COUNT = 4;
const MOBILE_MAX_BALLOON_COUNT = 5;
const MOBILE_MIN_VISIBLE_TOP_GAP = 8;
const MOBILE_ENTRY_DELAY_STEP = 0.64;
const MOBILE_MIN_NEARBY_DURATION_GAP = 2.6;
const DESKTOP_MAX_BALLOON_COUNT = 9;
const DESKTOP_MAX_BALLOON_COUNT_BINYAN3 = 8;
const DESKTOP_BREAKPOINT = 768;
const INITIAL_BALLOON_COUNT = 4;
const BALLOON_SPAWN_INTERVAL_MS = 900;
const TARGET_GAP_MS = 3000;
const MOBILE_TARGET_GAP_MS = 5000;
const MIN_BALLOON_TOP_GAP = 18;
const MIN_BALLOON_TOP_GAP_BINYAN3 = 21;
const MAX_BALLOON_TOP_GAP = 24;
const MAX_BALLOON_TOP_GAP_BINYAN3 = 28;
const NEARBY_BALLOON_TOP_GAP = 24;
const MIN_NEARBY_DURATION_GAP = 6;

const BALLOON_COLORS = [
  '#ffd7e8',
  '#fff0ad',
  '#e6d9ff',
  '#cfefff',
  '#d8f7d3',
  '#ffe0c2',
];

const PAST_DISTRACTOR_PRONOUNS = [
  { key: 'هُوَ', label: 'הוא' },
  { key: 'هِيَ', label: 'היא' },
  { key: 'هُنَّ', label: 'הן' },
];

const BINYAN2_PRESENT_FORM_I_DISTRACTOR_ROOTS = new Set(['جلس', 'رجع', 'عرف', 'نزل']);

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const normalizeBinyan = (value) => {
  const match = String(value || '').match(/\d+/);
  return match ? match[0] : String(value || '').trim();
};

const stripArabicDiacritics = (value) =>
  String(value || '')
    .replace(/[\u064B-\u065F\u0670\u06D6-\u06ED\u0640]/g, '')
    .replace(/\s+/g, '')
    .trim();

const normalizeWordKey = (value) => stripArabicDiacritics(value);

const normalizeRootKey = (value) =>
  stripArabicDiacritics(value).replace(/[^\u0621-\u064A]/g, '');

const isBinyan = (row, binyan) => normalizeBinyan(row['בניין'] || row.binyan) === String(binyan);

const isPastRow = (row) => String(row['זמן'] || row.tense || '').trim() === 'עבר';

const isWholeRow = (row) => String(row.gizra || row.gzira || row['גזרה'] || '').includes('שלמים');

const isAllowedBinyan3Gizra = (value) => {
  const gizra = String(value || '').replace(/\s+/g, '');
  return (
    gizra.includes('השלמים') ||
    gizra.includes('ע"ו') ||
    gizra.includes('ע"י') ||
    gizra.includes('פ"ו') ||
    gizra.includes('פ"י')
  );
};

const isAllowedGender = (allowedValue, genderCode) => {
  const normalized = String(allowedValue || '').trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === 'all') return true;
  return normalized
    .split(',')
    .map((part) => part.trim())
    .includes(genderCode);
};

const getTranslation = (row) => row.translation1 || row.translation2 || '';

const makeDisplay = ({ binyan, form, person = '' }) => ({
  binyan: `בניין ${binyan}`,
  form,
  person,
});

const formatDisplay = (display) =>
  [display?.binyan, display?.form, display?.person].filter(Boolean).join(', ');

const getActiveBalloonCount = (balloons) => (
  balloons.filter((balloon) => balloon.status !== 'leaving' && balloon.status !== 'wrong-leaving').length
);

const getOnscreenBalloonCount = (balloons) => (
  balloons.filter((balloon) => isBalloonOnscreen(balloon)).length
);

const isDesktopViewport = () => (
  typeof window === 'undefined' || window.innerWidth > DESKTOP_BREAKPOINT
);

const getBalloonLimits = (targetBinyan = '4') => {
  if (!isDesktopViewport()) {
    return {
      max: MOBILE_MAX_BALLOON_COUNT,
      targetOnscreen: MOBILE_TARGET_ONSCREEN_BALLOON_COUNT,
      allowEmergencyOverflow: false,
    };
  }
  return {
    max: targetBinyan === '3'
      ? DESKTOP_MAX_BALLOON_COUNT_BINYAN3
      : DESKTOP_MAX_BALLOON_COUNT,
    targetOnscreen: null,
    allowEmergencyOverflow: true,
  };
};

const parseDuration = (duration) => Number.parseFloat(String(duration || '0')) || 0;

const getAnimationNow = () => (
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
);

const getBalloonTopValue = (balloon) => (
  typeof balloon?.topValue === 'number'
    ? balloon.topValue
    : Number.parseFloat(String(balloon?.top || '0')) || 0
);

const getBalloonTravelPosition = (balloon) => {
  const now = getAnimationNow();
  const durationMs = Math.max(1, parseDuration(balloon.duration) * 1000);
  const delayMs = parseDuration(balloon.delay) * 1000;
  const createdAt = typeof balloon.createdAt === 'number' ? balloon.createdAt : now;
  const elapsedMs = now - createdAt - delayMs;
  const progress = Math.min(1, Math.max(0, elapsedMs / durationMs));
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const startX = viewportWidth + 20;
  const endX = -285;
  const sway = Number.parseFloat(String(balloon.sway || '0')) || 0;

  return {
    x: startX + ((endX - startX) * progress),
    y: sway * progress,
  };
};

const isBalloonOnscreen = (balloon) => {
  if (balloon.status === 'leaving' || balloon.status === 'wrong-leaving') return false;

  const { x } = getBalloonTravelPosition(balloon);
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const balloonSize = Number.parseFloat(String(balloon.size || '130')) || 130;
  return x < viewportWidth - 12 && x + balloonSize > 12;
};

const isMobileSpacingBalloon = (balloon) => {
  if (balloon.status === 'leaving' || balloon.status === 'wrong-leaving') return false;

  const { x } = getBalloonTravelPosition(balloon);
  const balloonSize = Number.parseFloat(String(balloon.size || '130')) || 130;
  return x + balloonSize > 12;
};

const isMobileFlowBlockingBalloon = (balloon) => {
  if (balloon.status === 'leaving' || balloon.status === 'wrong-leaving') return false;

  const { x } = getBalloonTravelPosition(balloon);
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 390;
  const balloonSize = Number.parseFloat(String(balloon.size || '130')) || 130;
  const centerX = x + balloonSize / 2;

  return x + balloonSize > 12 && centerX >= viewportWidth / 2;
};

const getMobileFlowBlockingBalloonCount = (balloons) => (
  balloons.filter((balloon) => isMobileFlowBlockingBalloon(balloon)).length
);

const hasOnscreenTarget = (balloons, poppedIds) => (
  balloons.some((balloon) => (
    balloon.item.isTarget &&
    !poppedIds.has(balloon.item.id) &&
    isBalloonOnscreen(balloon)
  ))
);

const makeBalloonLeave = (balloon, status = 'leaving') => {
  const { x, y } = getBalloonTravelPosition(balloon);
  const distanceToExit = Math.max(0, x + 285);
  const exitDuration = Math.min(3.2, Math.max(1.25, distanceToExit / 520));

  return {
    ...balloon,
    status,
    delay: '0s',
    exitStartX: `${x}px`,
    exitStartY: `${y}px`,
    exitDuration: `${exitDuration}s`,
  };
};

const getBalloonTopGap = (balloonSize, { targetBinyan = '4' } = {}) => {
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 760;
  const estimatedStageHeight = Math.max(390, viewportHeight - 152);
  const balloonHeight = balloonSize / 0.82;
  const isBinyan3 = targetBinyan === '3';
  const minGap = isBinyan3 ? MIN_BALLOON_TOP_GAP_BINYAN3 : MIN_BALLOON_TOP_GAP;
  const maxGap = isBinyan3 ? MAX_BALLOON_TOP_GAP_BINYAN3 : MAX_BALLOON_TOP_GAP;
  const gapMultiplier = isBinyan3 ? 84 : 72;

  return Math.min(maxGap, Math.max(minGap, (balloonHeight / estimatedStageHeight) * gapMultiplier));
};

const getBalloonTopRange = (balloonSize) => {
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 760;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 390;
  const mobileStageOffset = viewportWidth <= 480 ? 150 : 138;
  const estimatedStageHeight = isDesktopViewport()
    ? Math.max(460, viewportHeight - 152)
    : Math.max(viewportWidth <= 480 ? 390 : 430, viewportHeight - mobileStageOffset);
  const balloonHeight = balloonSize / 0.82;
  const minTop = isDesktopViewport() ? 8 : 6;
  const safeMaxTop = ((estimatedStageHeight - balloonHeight - 14) / estimatedStageHeight) * 100;
  const maxTop = Math.min(76, Math.max(isDesktopViewport() ? 68 : 48, safeMaxTop));

  return { minTop, maxTop };
};

const pickMobileBalloonTop = (activeBalloons, balloonSize) => {
  const { minTop, maxTop } = getBalloonTopRange(balloonSize);
  const spacingBalloons = activeBalloons.filter((balloon) => isMobileSpacingBalloon(balloon));
  const laneCount = MOBILE_MAX_BALLOON_COUNT;
  const lanes = Array.from({ length: laneCount }, (_, index) => (
    laneCount === 1
      ? (minTop + maxTop) / 2
      : minTop + ((maxTop - minTop) * index) / (laneCount - 1)
  ));
  const rankedLanes = lanes
    .map((laneTop) => {
      const nearestDistance = spacingBalloons.length
        ? Math.min(...spacingBalloons.map((balloon) => Math.abs(getBalloonTopValue(balloon) - laneTop)))
        : 100;
      const laneUsage = spacingBalloons.filter((balloon) => Math.abs(getBalloonTopValue(balloon) - laneTop) < 7).length;
      return { laneTop, nearestDistance, laneUsage };
    })
    .sort((a, b) => b.nearestDistance - a.nearestDistance || a.laneUsage - b.laneUsage);
  const selectedLane = rankedLanes[0]?.laneTop ?? (minTop + maxTop) / 2;
  const selectedDistance = rankedLanes[0]?.nearestDistance ?? 100;
  if (spacingBalloons.length > 0 && selectedDistance < MOBILE_MIN_VISIBLE_TOP_GAP) {
    return null;
  }

  const laneGap = laneCount > 1 ? (maxTop - minTop) / (laneCount - 1) : 0;
  const jitter = (Math.random() * 2 - 1) * Math.min(2.2, Math.max(1.2, laneGap * 0.1));
  const topWithJitter = Math.min(maxTop, Math.max(minTop, selectedLane + jitter));
  const jitteredDistance = spacingBalloons.length
    ? Math.min(...spacingBalloons.map((balloon) => Math.abs(getBalloonTopValue(balloon) - topWithJitter)))
    : 100;

  return jitteredDistance >= MOBILE_MIN_VISIBLE_TOP_GAP ? topWithJitter : selectedLane;
};

const pickBalloonTop = (currentVisible = [], balloonSize = 132, { targetBinyan = '4' } = {}) => {
  const activeBalloons = currentVisible.filter((balloon) => (
    balloon.status !== 'leaving' && balloon.status !== 'wrong-leaving'
  ));

  if (!isDesktopViewport()) {
    return pickMobileBalloonTop(activeBalloons, balloonSize);
  }

  const preferredGap = getBalloonTopGap(balloonSize, { targetBinyan });
  let bestTop = 8 + Math.random() * 68;
  let bestDistance = -1;

  for (let attempt = 0; attempt < 24; attempt += 1) {
    const candidate = 8 + Math.random() * 68;
    const nearestDistance = activeBalloons.length
      ? Math.min(...activeBalloons.map((balloon) => Math.abs(getBalloonTopValue(balloon) - candidate)))
      : 100;

    if (nearestDistance >= preferredGap) {
      return candidate;
    }

    if (nearestDistance > bestDistance) {
      bestDistance = nearestDistance;
      bestTop = candidate;
    }
  }

  return bestTop;
};

const pickBalloonDuration = (topValue, currentVisible = [], { slowerEntry = false, mobile = false } = {}) => {
  const minDuration = mobile ? (slowerEntry ? 18 : 15) : (slowerEntry ? 17 : 10.5);
  const maxDuration = mobile ? (slowerEntry ? 27 : 24) : 28;
  const baseDuration = mobile
    ? minDuration + Math.random() * (maxDuration - minDuration)
    : (slowerEntry ? 17 + Math.random() * 11 : 15 + Math.random() * 8);
  const nearby = currentVisible.filter((balloon) => (
    balloon.status !== 'leaving' &&
    balloon.status !== 'wrong-leaving' &&
    (!mobile || isMobileSpacingBalloon(balloon)) &&
    Math.abs(getBalloonTopValue(balloon) - topValue) < NEARBY_BALLOON_TOP_GAP
  ));

  if (nearby.length === 0) {
    return baseDuration;
  }

  const nearbyDurations = nearby.map((balloon) => parseDuration(balloon.duration));
  const durationOptions = mobile
    ? [
      minDuration + Math.random() * 2,
      minDuration + 2.6 + Math.random() * 2,
      minDuration + 5.2 + Math.random() * 2,
      minDuration + 7.8 + Math.random() * 1.2,
    ]
    : (slowerEntry
    ? [
      17 + Math.random() * 3,
      20.5 + Math.random() * 2.5,
      24 + Math.random() * 2.5,
      26.5 + Math.random() * 1.5,
    ]
    : [
      10.5 + Math.random() * 2.5,
      13 + Math.random() * 2,
      23 + Math.random() * 3,
      26 + Math.random() * 2,
    ]);
  const minimumDurationGap = mobile ? MOBILE_MIN_NEARBY_DURATION_GAP : MIN_NEARBY_DURATION_GAP;
  const safestDuration = durationOptions
    .map((option) => ({
      option,
      nearestGap: Math.min(...nearbyDurations.map((nearbyDuration) => Math.abs(nearbyDuration - option))),
    }))
    .sort((a, b) => b.nearestGap - a.nearestGap)[0];

  if (safestDuration.nearestGap >= minimumDurationGap) {
    return Math.min(maxDuration, Math.max(minDuration, safestDuration.option));
  }

  const nearestDuration = nearbyDurations
    .slice()
    .sort((a, b) => Math.abs(a - baseDuration) - Math.abs(b - baseDuration))[0];
  const speedOffset = baseDuration >= nearestDuration ? minimumDurationGap : -minimumDurationGap;
  return Math.min(maxDuration, Math.max(minDuration, nearestDuration + speedOffset + (Math.random() * 1.4 - 0.7)));
};

const pickUniqueByWord = (items, count, forbiddenWords = new Set()) => {
  const seen = new Set();
  const picked = [];

  for (const item of shuffleArray(items)) {
    const key = normalizeWordKey(item.word);
    if (!key || seen.has(key) || forbiddenWords.has(key)) continue;

    seen.add(key);
    picked.push(item);
    if (picked.length === count) break;
  }

  return picked;
};

const buildBinyan1Targets = (rows) => {
  const candidates = rows
    .filter((row) => isBinyan(row, 1) && isPastRow(row) && isWholeRow(row) && row.makor1)
    .map((row) => {
      const word = row.makor1;
      const root = normalizeRootKey(row['שורש']);
      const wordKey = normalizeWordKey(word);
      if (!wordKey || !root) return null;

      return {
        id: `target-b1-${root}-${wordKey}`,
        sourceKey: `target-b1-${root}-${wordKey}`,
        isTarget: true,
        word,
        root,
        translation: getTranslation(row),
        display: makeDisplay({ binyan: 1, form: 'מצדר' }),
      };
    })
    .filter(Boolean);

  return pickUniqueByWord(candidates, TARGET_COUNT);
};

const buildBinyan4Targets = (rows) => {
  const candidates = rows
    .filter((row) => isBinyan(row, 4) && isPastRow(row) && isWholeRow(row))
    .map((row) => {
      const word = buildGeneratedMasdar(row);
      const root = normalizeRootKey(row['שורש']);
      if (!word || !root) return null;

      return {
        id: `target-b4-${root}`,
        sourceKey: `target-b4-${root}`,
        isTarget: true,
        word,
        root,
        translation: getTranslation(row),
        display: makeDisplay({ binyan: 4, form: 'מצדר' }),
      };
    })
    .filter(Boolean);

  return pickUniqueByWord(candidates, TARGET_COUNT);
};

const buildBinyan2Targets = (rows) => {
  const candidates = rows
    .filter((row) => isBinyan(row, 2) && isPastRow(row))
    .map((row) => {
      const word = buildGeneratedMasdar(row);
      const root = normalizeRootKey(row['שורש']);
      if (!word || !root) return null;

      return {
        id: `target-b2-${root}`,
        sourceKey: `target-b2-${root}`,
        isTarget: true,
        word,
        root,
        translation: getTranslation(row),
        display: makeDisplay({ binyan: 2, form: 'מצדר' }),
      };
    })
    .filter(Boolean);

  return pickUniqueByWord(candidates, TARGET_COUNT);
};

const buildBinyan3Targets = (rows) => {
  const candidates = rows
    .filter((row) => (
      isBinyan(row, 3) &&
      isPastRow(row) &&
      isAllowedBinyan3Gizra(row.gizra || row.gzira || row['גזרה']) &&
      row.makor1
    ))
    .map((row) => {
      const word = row.makor1;
      const root = normalizeRootKey(row['שורש']);
      const wordKey = normalizeWordKey(word);
      if (!wordKey || !root) return null;

      return {
        id: `target-b3-${root}-${wordKey}`,
        sourceKey: `target-b3-${root}-${wordKey}`,
        isTarget: true,
        word,
        root,
        translation: getTranslation(row),
        display: makeDisplay({ binyan: 3, form: 'מצדר' }),
      };
    })
    .filter(Boolean);

  return pickUniqueByWord(candidates, TARGET_COUNT);
};

const buildMasdarDistractors = (rows, targetWords) => {
  const items = [];

  rows.forEach((row) => {
    if (!isPastRow(row)) return;
    const binyan = normalizeBinyan(row['בניין']);
    if (binyan === '1' && !isWholeRow(row)) return;
    let word = '';

    if (binyan === '1' || binyan === '3') {
      word = row.makor1 || '';
    } else if (binyan === '2') {
      word = buildGeneratedMasdar(row) || '';
    }

    const wordKey = normalizeWordKey(word);
    if (!wordKey || targetWords.has(wordKey)) return;

    if (['1', '2', '3'].includes(binyan)) {
      items.push({
        id: `distractor-masdar-${binyan}-${normalizeRootKey(row['שורש'])}-${wordKey}`,
        isTarget: false,
        word,
        root: normalizeRootKey(row['שורש']),
        translation: getTranslation(row),
        display: makeDisplay({ binyan, form: 'מצדר' }),
      });
    }
  });

  return items;
};

const buildBinyan1Distractors = (rows, targetWords) => {
  const items = [];

  rows
    .filter((row) => (
      isBinyan(row, 1) &&
      isPastRow(row) &&
      isWholeRow(row) &&
      String(row.R2_PAST || '').trim().toLowerCase() === 'a'
    ))
    .forEach((row) => {
      const word = row['פועל'];
      const wordKey = normalizeWordKey(word);
      const root = normalizeRootKey(row['שורש']);
      if (!wordKey || targetWords.has(wordKey)) return;

      items.push({
        id: `distractor-b1-past-huwa-${root}-${wordKey}`,
        isTarget: false,
        word,
        root,
        translation: getTranslation(row),
        display: makeDisplay({ binyan: 1, form: 'עבר', person: 'הוא' }),
      });
    });

  rows
    .filter((row) => isBinyan(row, 1) && isPastRow(row) && isWholeRow(row))
    .forEach((row) => {
      const allowed = String(row.benoni_poel_allowed || '').toLowerCase().trim();
      if (!(allowed === 'all' || allowed.split(',').map((value) => value.trim()).includes('sm'))) return;

      const participles = buildParticiplesFromRoot(row);
      const word = participles?.active?.masculine;
      const wordKey = normalizeWordKey(word);
      const root = normalizeRootKey(row['שורש']);
      if (!wordKey || targetWords.has(wordKey)) return;

      items.push({
        id: `distractor-b1-active-masculine-${root}-${wordKey}`,
        isTarget: false,
        word,
        root,
        translation: getTranslation(row),
        display: makeDisplay({ binyan: 1, form: 'בינוני פועל', person: 'יחיד' }),
      });
    });

  rows
    .filter((row) => (
      isBinyan(row, 1) &&
      isPastRow(row) &&
      isWholeRow(row) &&
      String(row.R2_FUTURE || '').trim().toLowerCase() === 'u'
    ))
    .forEach((row) => {
      const imperative = buildImperativeFromRow(row);
      const word = imperative?.you_ms;
      const wordKey = normalizeWordKey(word);
      const root = normalizeRootKey(row['שורש']);
      if (!wordKey || targetWords.has(wordKey)) return;

      items.push({
        id: `distractor-b1-imperative-you-ms-${root}-${wordKey}`,
        isTarget: false,
        word,
        root,
        translation: getTranslation(row),
        display: makeDisplay({ binyan: 1, form: 'ציווי', person: 'אתה' }),
      });
    });

  return pickUniqueByWord(items, items.length, targetWords);
};

const buildBinyan2Distractors = (rows, targetWords) => {
  const items = [];

  rows
    .filter((row) => isBinyan(row, 1) && isPastRow(row) && isWholeRow(row) && row.makor1)
    .forEach((row) => {
      const word = row.makor1;
      const wordKey = normalizeWordKey(word);
      const root = normalizeRootKey(row['שורש']);
      if (!wordKey || targetWords.has(wordKey)) return;

      items.push({
        id: `distractor-b1-masdar-${root}-${wordKey}`,
        isTarget: false,
        word,
        root,
        translation: getTranslation(row),
        display: makeDisplay({ binyan: 1, form: 'מצדר' }),
      });
    });

  rows
    .filter((row) => (
      isBinyan(row, 1) &&
      isPastRow(row) &&
      isWholeRow(row) &&
      BINYAN2_PRESENT_FORM_I_DISTRACTOR_ROOTS.has(normalizeRootKey(row['שורש']))
    ))
    .forEach((row) => {
      const present = conjugatePresentFormI_Salim_fromRow(row);
      const word = present?.['أَنْتَ'];
      const wordKey = normalizeWordKey(word);
      const root = normalizeRootKey(row['שורש']);
      if (!wordKey || targetWords.has(wordKey)) return;

      items.push({
        id: `distractor-b1-present-you-ms-${root}-${wordKey}`,
        isTarget: false,
        word,
        root,
        translation: getTranslation(row),
        display: makeDisplay({ binyan: 1, form: 'הווה-עתיד', person: 'אתה' }),
      });
    });

  return pickUniqueByWord(items, items.length, targetWords);
};

const buildBinyan4Distractors = (rows, targetWords) => {
  const items = [];
  const binyan4Rows = rows.filter((row) => isBinyan(row, 4) && isPastRow(row) && isWholeRow(row));

  binyan4Rows.forEach((row) => {
    const root = normalizeRootKey(row['שורש']);
    const translation = getTranslation(row);

    const past = conjugatePastGeneric_Salim(row['פועל']);
    PAST_DISTRACTOR_PRONOUNS.forEach(({ key, label }) => {
      const word = past?.[key];
      if (!word || targetWords.has(normalizeWordKey(word))) return;
      items.push({
        id: `distractor-b4-past-${root}-${key}`,
        isTarget: false,
        word,
        root,
        translation,
        display: makeDisplay({ binyan: 4, form: 'עבר', person: label }),
      });
    });

    const present = conjugatePresentFormIV_Salim_fromRow(row);
    if (present?.['أَنَا'] && !targetWords.has(normalizeWordKey(present['أَنَا']))) {
      items.push({
        id: `distractor-b4-present-${root}-ana`,
        isTarget: false,
        word: present['أَنَا'],
        root,
        translation,
        display: makeDisplay({ binyan: 4, form: 'הווה-עתיד', person: 'אני' }),
      });
    }

    const imperative = buildImperativeFromRowIV(row);
    [
      { key: 'you_ms', label: 'אתה' },
      { key: 'you_mp', label: 'אתם' },
    ].forEach(({ key, label }) => {
      const word = imperative?.[key];
      if (!word || targetWords.has(normalizeWordKey(word))) return;
      items.push({
        id: `distractor-b4-imperative-${root}-${key}`,
        isTarget: false,
        word,
        root,
        translation,
        display: makeDisplay({ binyan: 4, form: 'ציווי', person: label }),
      });
    });

    const participles = buildParticiplesFromRowNonFormI(row);
    if (participles?.active) {
      [
        { key: 'masculine', code: 'sm', label: 'יחיד' },
        { key: 'pluralFeminine', code: 'pf', label: 'רבות' },
      ].forEach(({ key, code, label }) => {
        const word = participles.active[key];
        if (!word || !isAllowedGender(row.benoni_poel_allowed, code) || targetWords.has(normalizeWordKey(word))) return;
        items.push({
          id: `distractor-b4-active-${root}-${key}`,
          isTarget: false,
          word,
          root,
          translation,
          display: makeDisplay({ binyan: 4, form: 'בינוני פועל', person: label }),
        });
      });
    }

    if (participles?.passive) {
      [
        { key: 'masculine', code: 'sm', label: 'יחיד' },
        { key: 'pluralFeminine', code: 'pf', label: 'רבות' },
      ].forEach(({ key, code, label }) => {
        const word = participles.passive[key];
        if (!word || !isAllowedGender(row.benoni_paul_allowed, code) || targetWords.has(normalizeWordKey(word))) return;
        items.push({
          id: `distractor-b4-passive-${root}-${key}`,
          isTarget: false,
          word,
          root,
          translation,
          display: makeDisplay({ binyan: 4, form: 'בינוני פעול', person: label }),
        });
      });
    }
  });

  return pickUniqueByWord(items, items.length, targetWords);
};

const buildBinyan3Distractors = (rows, targetWords) => {
  const items = [];
  const binyan3Rows = rows.filter((row) => (
    isBinyan(row, 3) &&
    isPastRow(row) &&
    isAllowedBinyan3Gizra(row.gizra || row.gzira || row['גזרה'])
  ));

  binyan3Rows.forEach((row) => {
    const root = normalizeRootKey(row['שורש']);
    const translation = getTranslation(row);
    const pastWord = row['פועל'];

    if (pastWord && !targetWords.has(normalizeWordKey(pastWord))) {
      items.push({
        id: `distractor-b3-past-${root}-${normalizeWordKey(pastWord)}`,
        isTarget: false,
        word: pastWord,
        root,
        translation,
        display: makeDisplay({ binyan: 3, form: 'עבר', person: 'נסתר' }),
      });
    }

    const imperative = buildImperativeFromRowIII(row);
    if (imperative?.you_ms && !targetWords.has(normalizeWordKey(imperative.you_ms))) {
      items.push({
        id: `distractor-b3-imperative-${root}-${normalizeWordKey(imperative.you_ms)}`,
        isTarget: false,
        word: imperative.you_ms,
        root,
        translation,
        display: makeDisplay({ binyan: 3, form: 'ציווי', person: 'נוכח' }),
      });
    }

    const participles = buildParticiplesFromRowNonFormI(row);
    const activeFeminine = participles?.active?.feminine;
    if (
      activeFeminine &&
      isAllowedGender(row.benoni_poel_allowed, 'sf') &&
      !targetWords.has(normalizeWordKey(activeFeminine))
    ) {
      items.push({
        id: `distractor-b3-active-feminine-${root}-${normalizeWordKey(activeFeminine)}`,
        isTarget: false,
        word: activeFeminine,
        root,
        translation,
        display: makeDisplay({ binyan: 3, form: 'בינוני פועל', person: 'יחידה' }),
      });
    }
  });

  rows
    .filter((row) => isBinyan(row, 2) && isPastRow(row))
    .forEach((row) => {
      const root = normalizeRootKey(row['שורש']);
      const translation = getTranslation(row);
      const participles = buildParticiplesFromRowNonFormI(row);
      const passiveFeminine = participles?.passive?.feminine;

      if (
        passiveFeminine &&
        isAllowedGender(row.benoni_paul_allowed, 'sf') &&
        !targetWords.has(normalizeWordKey(passiveFeminine))
      ) {
        items.push({
          id: `distractor-b2-passive-feminine-${root}-${normalizeWordKey(passiveFeminine)}`,
          isTarget: false,
          word: passiveFeminine,
          root,
          translation,
          display: makeDisplay({ binyan: 2, form: 'בינוני פעול', person: 'יחידה' }),
        });
      }
    });

  return pickUniqueByWord(items, items.length, targetWords);
};

function MasdarBalloonGameBinyan4({ gameConfig = { binyan: '4' } }) {
  const navigate = useNavigate();
  const { grade } = useParams();
  const location = useLocation();
  const fromBinyan = location.state?.fromBinyan;
  const targetBinyan = normalizeBinyan(gameConfig.binyan || '4') || '4';
  const targetBinyanLabel = `בניין ${targetBinyan}`;

  const [rows, setRows] = useState([]);
  const [targets, setTargets] = useState([]);
  const [distractors, setDistractors] = useState([]);
  const [visibleBalloons, setVisibleBalloons] = useState([]);
  const [poppedTargetIds, setPoppedTargetIds] = useState([]);
  const [mistakes, setMistakes] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showResultsPopup, setShowResultsPopup] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);

  const balloonSerialRef = useRef(0);
  const timersRef = useRef([]);
  const audioContextRef = useRef(null);
  const resultsSummaryRef = useRef(null);
  const poppedTargetIdsRef = useRef([]);
  const noTargetSinceRef = useRef(null);

  const getBackUrl = () => {
    if (fromBinyan) return `/grade/${grade}/verbs/binyan/${fromBinyan}`;
    return `/grade/${grade}/verbs`;
  };

  const initAudioContext = useCallback(async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  const playSuccessSound = useCallback(() => {
    try {
      initAudioContext().then((audioContext) => {
      if (!audioContext) return;

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 523.25;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      });
    } catch (error) {
      console.log('Audio not supported');
    }
  }, [initAudioContext]);

  const playErrorSound = useCallback(() => {
    try {
      initAudioContext().then((audioContext) => {
      if (!audioContext) return;

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 200;
      oscillator.type = 'sawtooth';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
      });
    } catch (error) {
      console.log('Audio not supported');
    }
  }, [initAudioContext]);

  const schedule = useCallback((callback, delay) => {
    const timer = window.setTimeout(callback, delay);
    timersRef.current.push(timer);
    return timer;
  }, []);

  useEffect(() => () => {
    timersRef.current.forEach((timer) => window.clearTimeout(timer));
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
  }, []);

  useEffect(() => {
    poppedTargetIdsRef.current = poppedTargetIds;
  }, [poppedTargetIds]);

  const createBalloon = useCallback((item, { scatter = false, currentVisible = [], entryDelay = 0 } = {}) => {
    const wordLength = normalizeWordKey(item.word).length;
    const size = Math.min(186, 116 + Math.random() * 28 + Math.max(0, wordLength - 4) * 18);
    const isMobileViewport = !isDesktopViewport();
    const topValue = pickBalloonTop(currentVisible, size, { targetBinyan });
    if (topValue === null) return null;

    const duration = pickBalloonDuration(topValue, currentVisible, {
      slowerEntry: !scatter,
      mobile: isMobileViewport,
    });
    const scatterProgress = isDesktopViewport()
      ? 0.24 + Math.random() * 0.3
      : 0.14 + Math.random() * 0.18;
    const delay = scatter ? -(duration * scatterProgress) : entryDelay;
    const color = BALLOON_COLORS[balloonSerialRef.current % BALLOON_COLORS.length];

    balloonSerialRef.current += 1;

    return {
      id: `balloon-${balloonSerialRef.current}`,
      item,
      status: 'normal',
      top: `${topValue}%`,
      topValue,
      size: `${size}px`,
      duration: `${duration}s`,
      delay: `${delay}s`,
      sway: `${-45 + Math.random() * 90}px`,
      color,
      createdAt: getAnimationNow(),
    };
  }, [targetBinyan]);

  const chooseNextItem = useCallback((currentVisible, poppedIds, { forceTarget = false, allowTarget = true } = {}) => {
    const visibleItemIds = new Set(currentVisible.map((balloon) => balloon.item.id));
    const availableTargets = targets.filter((target) => !poppedIds.has(target.id) && !visibleItemIds.has(target.id));

    if (forceTarget && availableTargets.length > 0) {
      return shuffleArray(availableTargets)[0];
    }

    if (allowTarget && availableTargets.length > 0 && Math.random() < 0.28) {
      return shuffleArray(availableTargets)[0];
    }

    const availableDistractors = distractors.filter((distractor) => !visibleItemIds.has(distractor.id));
    return shuffleArray(availableDistractors)[0] || shuffleArray(distractors)[0] || availableTargets[0] || null;
  }, [distractors, targets]);

  const fillVisibleBalloons = useCallback((baseVisible, poppedIds, count, { scatter = false } = {}) => {
    const next = [...baseVisible];
    const now = getAnimationNow();
    for (let i = 0; i < count; i += 1) {
      const targetOnscreen = hasOnscreenTarget(next, poppedIds);
      if (targetOnscreen) {
        noTargetSinceRef.current = null;
      } else if (noTargetSinceRef.current === null) {
        noTargetSinceRef.current = now;
      }

      const forceTarget = (
        !targetOnscreen &&
        noTargetSinceRef.current !== null &&
        now - noTargetSinceRef.current >= (isDesktopViewport() ? TARGET_GAP_MS : MOBILE_TARGET_GAP_MS)
      );
      const item = chooseNextItem(next, poppedIds, {
        forceTarget,
        allowTarget: true,
      });
      if (!item) break;
      const balloon = createBalloon(item, {
        scatter,
        currentVisible: next,
        entryDelay: scatter ? 0 : i * (isDesktopViewport() ? 0.42 : MOBILE_ENTRY_DELAY_STEP),
      });
      if (!balloon) break;

      if (item.isTarget) {
        noTargetSinceRef.current = null;
      }
      next.push(balloon);
    }
    return next;
  }, [chooseNextItem, createBalloon]);

  const startNewGame = useCallback((allRows) => {
    timersRef.current.forEach((timer) => window.clearTimeout(timer));
    timersRef.current = [];
    poppedTargetIdsRef.current = [];
    noTargetSinceRef.current = null;

    const pickedTargets = targetBinyan === '1'
      ? buildBinyan1Targets(allRows)
      : targetBinyan === '2'
        ? buildBinyan2Targets(allRows)
        : targetBinyan === '3'
          ? buildBinyan3Targets(allRows)
          : buildBinyan4Targets(allRows);
    if (pickedTargets.length < TARGET_COUNT) {
      setLoadError(`לא נמצאו מספיק מצדרים של ${targetBinyanLabel} כדי לבנות את משחק הבלונים.`);
      setTargets([]);
      setDistractors([]);
      setVisibleBalloons([]);
      return;
    }

    const targetWords = new Set(pickedTargets.map((target) => normalizeWordKey(target.word)));
    const pickedDistractors = targetBinyan === '1'
      ? shuffleArray(buildBinyan1Distractors(allRows, targetWords))
      : targetBinyan === '2'
        ? shuffleArray(buildBinyan2Distractors(allRows, targetWords))
        : targetBinyan === '3'
          ? shuffleArray(buildBinyan3Distractors(allRows, targetWords))
          : shuffleArray([
            ...buildMasdarDistractors(allRows, targetWords),
            ...buildBinyan4Distractors(allRows, targetWords),
          ]);

    if (pickedDistractors.length < getBalloonLimits(targetBinyan).max) {
      setLoadError('לא נמצאו מספיק מסיחים כדי לבנות את משחק הבלונים.');
      setTargets([]);
      setDistractors([]);
      setVisibleBalloons([]);
      return;
    }

    balloonSerialRef.current = 0;
    setLoadError('');
    setTargets(pickedTargets);
    setDistractors(pickedDistractors);
    setPoppedTargetIds([]);
    setMistakes([]);
    setShowResults(false);
    setShowResultsPopup(false);
    setShowFireworks(false);

    const initialBalloons = [];
    const initialBalloonCount = Math.min(INITIAL_BALLOON_COUNT, getBalloonLimits(targetBinyan).max);
    for (let i = 0; i < initialBalloonCount; i += 1) {
      const item = (() => {
        const visibleItemIds = new Set(initialBalloons.map((balloon) => balloon.item.id));
        const availableTargets = pickedTargets.filter((target) => !visibleItemIds.has(target.id));
        if (i === 0 && availableTargets.length > 0) return shuffleArray(availableTargets)[0];
        if (i > 1 && availableTargets.length > 0 && Math.random() < 0.25) return shuffleArray(availableTargets)[0];
        const availableDistractors = pickedDistractors.filter((distractor) => !visibleItemIds.has(distractor.id));
        return shuffleArray(availableDistractors)[0] || shuffleArray(pickedDistractors)[0] || availableTargets[0];
      })();
      if (item) {
        const balloon = createBalloon(item, { scatter: true, currentVisible: initialBalloons });
        if (balloon) initialBalloons.push(balloon);
      }
    }

    const hasTargetVisible = initialBalloons.some((balloon) => balloon.item.isTarget);
    if (!hasTargetVisible && pickedTargets.length > 0) {
      const targetBalloon = createBalloon(pickedTargets[0], {
        scatter: true,
        currentVisible: initialBalloons.slice(1),
      });
      if (targetBalloon) {
        initialBalloons[0] = targetBalloon;
      }
    }

    setVisibleBalloons(initialBalloons);
  }, [createBalloon, targetBinyan, targetBinyanLabel]);

  useEffect(() => {
    const load = async () => {
      try {
        const basePath = process.env.PUBLIC_URL || '';
        const path = `${basePath}/verbs-grade10.csv`.replace(/([^:]\/)\/+/g, '$1');
        const data = await loadVerbsCsv(path);
        setRows(data);
        startNewGame(data);
      } catch (error) {
        console.error('Failed to load verbs CSV:', error);
        setLoadError('שגיאה בטעינת הנתונים למשחק הבלונים.');
      } finally {
        setIsLoaded(true);
      }
    };

    load();
  }, [startNewGame]);

  useEffect(() => {
    if (!isLoaded || loadError || showResults || targets.length === 0 || distractors.length === 0) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setVisibleBalloons((prev) => {
        const popped = new Set(poppedTargetIdsRef.current);
        const targetOnscreen = hasOnscreenTarget(prev, popped);
        const now = getAnimationNow();

        if (targetOnscreen) {
          noTargetSinceRef.current = null;
        } else if (noTargetSinceRef.current === null) {
          noTargetSinceRef.current = now;
        }

        const { max, targetOnscreen: targetOnscreenGoal, allowEmergencyOverflow } = getBalloonLimits(targetBinyan);
        const isMobileViewport = !isDesktopViewport();
        const activeCount = getActiveBalloonCount(prev);
        const onscreenCount = getOnscreenBalloonCount(prev);
        const mobileBlockingCount = isMobileViewport
          ? getMobileFlowBlockingBalloonCount(prev)
          : activeCount;
        const mobileNeedsMoreBalloons = isMobileViewport && onscreenCount < targetOnscreenGoal;
        const mobileCanStartNextWave = isMobileViewport && mobileBlockingCount < targetOnscreenGoal;
        const targetGapExceeded = (
          !targetOnscreen &&
          noTargetSinceRef.current !== null &&
          now - noTargetSinceRef.current >= (isMobileViewport ? MOBILE_TARGET_GAP_MS : TARGET_GAP_MS)
        );
        const capacityCount = isMobileViewport ? mobileBlockingCount : activeCount;
        const normalCapacity = Math.max(0, max - capacityCount);
        const targetEmergencyCapacity = targetGapExceeded && allowEmergencyOverflow
          ? Math.max(0, max + 1 - activeCount)
          : 0;
        const spawnCapacity = Math.max(normalCapacity, targetEmergencyCapacity);

        if (spawnCapacity <= 0) {
          return prev;
        }

        const mobileDesiredSpawnCount = mobileNeedsMoreBalloons
          ? targetOnscreenGoal - onscreenCount
          : (mobileCanStartNextWave || (targetGapExceeded && activeCount < max) ? 1 : 0);
        const spawnCount = isMobileViewport
          ? Math.min(spawnCapacity, mobileDesiredSpawnCount)
          : 1;

        if (spawnCount <= 0) {
          return prev;
        }

        return fillVisibleBalloons(prev, popped, spawnCount, { scatter: false });
      });
    }, isDesktopViewport() ? BALLOON_SPAWN_INTERVAL_MS : Math.floor(BALLOON_SPAWN_INTERVAL_MS / 2));

    return () => window.clearInterval(interval);
  }, [distractors.length, fillVisibleBalloons, isLoaded, loadError, showResults, targetBinyan, targets.length]);

  const handleCorrectBalloon = useCallback((balloon) => {
    if (poppedTargetIds.includes(balloon.item.id)) return;

    playSuccessSound();
    setShowFireworks(true);
    schedule(() => setShowFireworks(false), 1100);

    const nextPoppedIds = [...poppedTargetIds, balloon.item.id];
    setPoppedTargetIds(nextPoppedIds);

    setVisibleBalloons((prev) => {
      const withoutClicked = prev.filter((entry) => entry.id !== balloon.id);
      if (hasOnscreenTarget(withoutClicked, new Set(nextPoppedIds))) {
        noTargetSinceRef.current = null;
      } else {
        noTargetSinceRef.current = getAnimationNow();
      }
      return withoutClicked;
    });

    if (nextPoppedIds.length >= TARGET_COUNT) {
      schedule(() => setShowResults(true), 1200);
    }
  }, [playSuccessSound, poppedTargetIds, schedule]);

  const handleWrongBalloon = useCallback((balloon) => {
    if (balloon.status !== 'normal') return;

    playErrorSound();
    setMistakes((prev) => [
      ...prev,
      {
        word: balloon.item.word,
        display: balloon.item.display,
      },
    ]);

    setVisibleBalloons((prev) =>
      prev.map((entry) => (
        entry.id === balloon.id ? { ...entry, status: 'wrong' } : entry
      ))
    );

    schedule(() => {
      setVisibleBalloons((prev) =>
        prev.map((entry) => (
          entry.id === balloon.id ? makeBalloonLeave(entry, 'wrong-leaving') : entry
        ))
      );
    }, 500);
  }, [playErrorSound, schedule]);

  const handleBalloonTravelEnd = useCallback((event, balloon) => {
    if (!['masdarBalloonTravel', 'masdarBalloonExit'].includes(event.animationName)) return;

    setVisibleBalloons((prev) => {
      if (!prev.some((entry) => entry.id === balloon.id)) return prev;
      const withoutFinished = prev.filter((entry) => entry.id !== balloon.id);
      const popped = new Set(poppedTargetIdsRef.current);
      if (hasOnscreenTarget(withoutFinished, popped)) {
        noTargetSinceRef.current = null;
      } else if (noTargetSinceRef.current === null) {
        noTargetSinceRef.current = getAnimationNow();
      }
      return withoutFinished;
    });
  }, []);

  const primeAudio = () => {
    initAudioContext();
  };

  const handleBalloonClick = (balloon) => {
    if (showResults || balloon.status !== 'normal') return;
    if (balloon.item.isTarget) {
      handleCorrectBalloon(balloon);
      return;
    }
    handleWrongBalloon(balloon);
  };

  const captureResultsImage = async () => {
    if (!resultsSummaryRef.current) return null;
    const canvas = await html2canvas(resultsSummaryRef.current, {
      backgroundColor: '#5eb9c5',
      scale: 2,
      useCORS: true,
    });
    return canvas.toDataURL('image/png');
  };

  const shareViaEmail = async () => {
    const imageData = await captureResultsImage();
    if (!imageData) return;
    const link = document.createElement('a');
    link.download = `תוצאות-בלוני-מצדרים-${targetBinyanLabel}.png`;
    link.href = imageData;
    link.click();
  };

  const dataURLtoBlob = (dataURL) => {
    const parts = dataURL.split(',');
    const mime = parts[0].match(/:(.*?);/)[1];
    const bstr = atob(parts[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i += 1) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new Blob([u8arr], { type: mime });
  };

  const shareViaWhatsApp = async () => {
    const imageData = await captureResultsImage();
    if (!imageData) return;

    const fileName = `תוצאות-בלוני-מצדרים-${targetBinyanLabel}.png`;
    const blob = dataURLtoBlob(imageData);
    const file = new File([blob], fileName, { type: 'image/png' });

    if (
      typeof navigator.share === 'function' &&
      typeof navigator.canShare === 'function' &&
      navigator.canShare({ files: [file] })
    ) {
      try {
        await navigator.share({
          title: `תוצאות בלוני מצדרים ${targetBinyanLabel}`,
          text: `בלוני מצדרים ${targetBinyanLabel}`,
          files: [file],
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('שגיאה בשיתוף:', err);
        }
      }
    } else {
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      link.download = fileName;
      link.href = objectUrl;
      link.click();
      URL.revokeObjectURL(objectUrl);
    }

    setShowResultsPopup(false);
  };

  if (grade !== 'grade11' && !(grade === 'grade10' && targetBinyan === '1')) {
    return (
      <div className="masdar-balloon-game loading">
        {targetBinyan === '1'
          ? 'המשחק הזה זמין כרגע רק לכיתות י׳ וי״א.'
          : 'המשחק הזה זמין כרגע רק לכיתה י"א.'}
      </div>
    );
  }

  if (!isLoaded) {
    return <div className="masdar-balloon-game loading">טוען...</div>;
  }

  if (loadError) {
    return (
      <div className="masdar-balloon-game loading">
        <button
          onClick={() => navigate(getBackUrl())}
          className="back-to-grade-button"
          style={{ position: 'relative', margin: '0 auto 16px', display: 'block', left: 'auto', transform: 'none', top: 'auto' }}
        >
          חזרה למשחקי הפועל ↩
        </button>
        {loadError}
      </div>
    );
  }

  if (showResults) {
    const finalScore = poppedTargetIds.length - mistakes.length;
    const scoreLabel = finalScore < 0
      ? `מינוס ${Math.abs(finalScore)}`
      : `${finalScore}/${TARGET_COUNT}`;

    return (
      <div className="App">
        <header className="App-header">
          <div
            className="header-with-back"
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}
          >
            <button
              onClick={() => navigate(getBackUrl())}
              className="back-to-grade-button"
              style={{ position: 'absolute', right: '20px', top: '-44px' }}
            >
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>
              תוצאות המשחק
            </h1>
          </div>
        </header>
        <main className="game-container results-screen">
          <div className="words-summary" ref={resultsSummaryRef}>
            <h3>בלוני מצדרים - {targetBinyanLabel}</h3>
            <div className="final-score-display">
              ניקוד: {scoreLabel}
            </div>
            <br />

            <div className="words-list">
              {targets.map((target, index) => (
                <div key={`target-${target.id}`} className="word-item">
                  <div className="word-pair">
                    <span className="arabic-word-summary">{target.word}</span>
                    <span className="separator"> - </span>
                    <span className="hebrew-word">{targetBinyanLabel}, מצדר</span>
                  </div>
                  <div className="attempts-info">
                    <span>✓ בלון מצדר {index + 1}</span>
                  </div>
                </div>
              ))}
              {mistakes.map((mistake, index) => (
                <div key={`mistake-${mistake.word}-${index}`} className="word-item">
                  <div className="word-pair">
                    <span className="arabic-word-summary">{mistake.word}</span>
                    <span className="separator"> - </span>
                    <span className="hebrew-word">{formatDisplay(mistake.display)}</span>
                  </div>
                  <div className="attempts-info multiple-attempts">
                    <span>✗ אינו צורת המקור</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="game-end-buttons">
              <button onClick={() => startNewGame(rows)} className="game-button restart-button">
                משחק חדש
              </button>
              <button onClick={() => setShowResultsPopup(true)} className="game-button send-button">
                שליחת תוצאות
              </button>
            </div>
          </div>

          {showResultsPopup && (
            <div className="popup-overlay" onClick={() => setShowResultsPopup(false)}>
              <div className="popup-content" onClick={(event) => event.stopPropagation()}>
                <button className="popup-close-button" onClick={() => setShowResultsPopup(false)}>
                  ×
                </button>
                <h3>שתף את התוצאות</h3>
                <p>בחר איך תרצה לשתף את תוצאות המשחק.</p>
                <div className="popup-buttons">
                  <button onClick={shareViaEmail} className="popup-button email-button">
                    📷 הורד תמונה
                  </button>
                  <button onClick={shareViaWhatsApp} className="popup-button whatsapp-button">
                    💬 שתף בוואטסאפ
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="masdar-balloon-game">
      <header className="masdar-balloon-header">
        <button
          onClick={() => navigate(getBackUrl())}
          className="back-to-grade-button"
          style={{ position: 'relative', margin: '0 auto 5px', display: 'block', left: 'auto', transform: 'none', top: 'auto' }}
        >
          חזרה למשחקי הפועל ↩
        </button>
        <h1>פוצץ את עשרת הבלונים המכילים מצדר של {targetBinyanLabel}</h1>
        <div className="masdar-balloon-score-row">
          <span>{poppedTargetIds.length}/{TARGET_COUNT}</span>
        </div>
      </header>

      <main className="masdar-balloon-stage" aria-live="polite">
        {visibleBalloons.map((balloon) => {
          const isWrong = balloon.status === 'wrong' || balloon.status === 'wrong-leaving';
          const isLeaving = balloon.status === 'leaving' || balloon.status === 'wrong-leaving';

          return (
            <div
              key={balloon.id}
              className={`masdar-balloon-flight${isWrong ? ' wrong' : ''}${isLeaving ? ' leaving' : ''}`}
              style={{
                '--balloon-top': balloon.top,
                '--balloon-duration': balloon.duration,
                '--balloon-delay': balloon.delay,
                '--balloon-sway': balloon.sway,
                '--balloon-exit-start-x': balloon.exitStartX,
                '--balloon-exit-start-y': balloon.exitStartY,
                '--balloon-exit-duration': balloon.exitDuration,
              }}
              onAnimationEnd={(event) => handleBalloonTravelEnd(event, balloon)}
            >
              <button
                type="button"
                className={`masdar-balloon${isWrong ? ' wrong' : ''}`}
                style={{
                  '--balloon-size': balloon.size,
                  '--balloon-color': balloon.color,
                }}
                onPointerDown={primeAudio}
                onClick={() => handleBalloonClick(balloon)}
              >
                <span className="masdar-balloon-highlight" />
                <span className="masdar-balloon-word">{balloon.item.word}</span>
                <span className="masdar-balloon-knot" />
              </button>
            </div>
          );
        })}
      </main>

      {showFireworks && (
        <div className="fireworks-container">
          <div className="firework firework-1"></div>
          <div className="firework firework-2"></div>
          <div className="firework firework-3"></div>
          <div className="firework firework-4"></div>
          <div className="firework firework-5"></div>
          <div className="firework firework-6"></div>
          <div className="firework firework-7"></div>
          <div className="firework firework-8"></div>
          <div className="firework firework-9"></div>
          <div className="firework firework-10"></div>
          <div className="firework firework-11"></div>
          <div className="firework firework-12"></div>
        </div>
      )}
    </div>
  );
}

export default MasdarBalloonGameBinyan4;
